/**
 * The ten single-use backup codes, shown exactly once.
 *
 * `POST /auth/2fa/enroll/confirm` stores them Argon2-hashed (`app/routes/admin.py`),
 * so this render is the only moment they exist in readable form anywhere. There
 * is no "show them again" endpoint and there never will be: recovering from a
 * lost set means a superadmin wiping 2FA and the user enrolling afresh.
 *
 * Two consequences the markup has to honour. The warning is loud and it comes
 * FIRST, before the codes, because a user who has already closed the page
 * cannot be warned. And nothing here may unmount itself on success, timeout, or
 * a parent's re-render — see `TwoFactorSetup`.
 */
import { useEffect, useState } from 'react'

import { useI18n } from '../i18n'

type CopyState = 'idle' | 'copied' | 'failed'

export function BackupCodes({ codes }: { codes: string[] }) {
  const { t } = useI18n()
  const [copyState, setCopyState] = useState<CopyState>('idle')

  /**
   * One reflexive Cmd-R or tab-close while these are on screen destroys them
   * permanently -- there is no re-issue endpoint, and recovering means a
   * superadmin wiping 2FA and a full re-enrolment. The browser's own "leave
   * site?" prompt is the only thing that can interrupt that keystroke. It is
   * gone the moment this unmounts, which is the acknowledgement.
   */
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Legacy browsers need the assignment; modern ones only need the default
      // prevented. Neither shows this string -- the prompt's wording is the
      // browser's, not ours.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [])

  // A data: URI rather than a blob: no object URL to create, revoke or leak,
  // and the codes never touch an API the browser can deny. This is the escape
  // hatch for anywhere `navigator.clipboard` is undefined -- an insecure
  // origin, or a browser with the permission switched off -- where the only
  // other answer is "select them by hand".
  const download = `data:text/plain;charset=utf-8,${encodeURIComponent(codes.join('\n') + '\n')}`

  async function copy() {
    // Not `navigator.clipboard.writeText(...)` unguarded: the API is undefined
    // on an insecure origin and can reject outright when permission is denied.
    // Either way the codes stay on screen and the user is told to copy by hand.
    const clipboard = navigator.clipboard
    if (!clipboard?.writeText) {
      setCopyState('failed')
      return
    }
    try {
      await clipboard.writeText(codes.join('\n'))
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
  }

  return (
    <div className="space-y-3 rounded-lg border border-degraded/40 bg-degraded/5 p-4">
      <p role="alert" className="text-sm font-medium text-degraded">
        {t('account.backupCodes.warning')}
      </p>

      {/*
        auto-fit + a minimum track, NOT a fixed column count. Tailwind's
        `grid-cols-5` compiles to `repeat(5, minmax(0, 1fr))`, and that `0`
        floor lets a column shrink below its content: at a panel width under
        ~570px the track fell to 82px against a code that needs 84.3px, so the
        digits rendered outside the warning box. A 7.5rem floor is wider than
        any 10-digit code at this font size, so the grid drops to four, three
        or two columns instead of clipping — and the count still adapts to the
        space, which a hardcoded `sm:grid-cols-5` never did.
      */}
      <ul className="grid grid-cols-[repeat(auto-fit,minmax(7.5rem,1fr))] gap-x-4 gap-y-1">
        {codes.map((code) => (
          <li key={code} className="num select-all text-sm text-fg">
            {code}
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void copy()}
          className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg
                     hover:bg-bg"
        >
          {t('account.backupCodes.copy')}
        </button>
        <a
          href={download}
          download="tikhub-backup-codes.txt"
          className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg
                     hover:bg-bg"
        >
          {t('account.backupCodes.download')}
        </a>
        <p
          data-testid="copy-status"
          role="status"
          className={`text-xs ${copyState === 'failed' ? 'text-down' : 'text-muted'}`}
        >
          {copyState === 'copied' && t('account.backupCodes.copied')}
          {copyState === 'failed' && t('account.backupCodes.copyFailed')}
        </p>
      </div>
    </div>
  )
}
